import { useMemo, useState } from 'react';
import { isNil, isNotNil } from '~/utilities/type-guards';
import { getContactIdentities } from './contactIdentity';
import type { ContactIdentity } from './contactIdentity';

const getUrlIdentity = (url: string, accountHashedId?: string): ContactIdentity | null => {
  if (isNil(accountHashedId)) {
    return null;
  }

  const params = new URL(url).search;
  const urlParams = new URLSearchParams(params);
  const uniqueId = urlParams.get('uid');
  const visitorKey = urlParams.get('vk');

  if (isNil(uniqueId) || isNil(visitorKey)) {
    return null;
  }

  return { accountHashedId, lastUsed: Date.now(), uniqueId, visitorKey };
};

export const useGetContactIdentities = ({
  accountHashedId,
  url,
}: {
  accountHashedId?: string;
  url: string;
}): [ContactIdentity[], () => void] => {
  const [refreshKey, setRefreshKey] = useState(0);

  const identities = useMemo(() => {
    const urlIdentity = getUrlIdentity(url, accountHashedId);
    return isNotNil(urlIdentity) ? [urlIdentity] : getContactIdentities(accountHashedId);
    // need to include the refreshKey so we can re-run when the refresh function is called.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, accountHashedId, refreshKey]);

  const refresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  return [identities, refresh];
};
